import { useId, useMemo, useState } from "react";
import type { KeyboardEvent } from "react";
import type { Catalog as CatalogData, Category, Plan } from "./types";
import { money, plainText } from "./format";
import { productTitle as backendProductTitle } from "./product";
import "./storefront.css";

interface CatalogProps {
  catalog: CatalogData | null;
  loading: boolean;
  error: string;
  onRetry: () => void;
  onSelect: (plan: Plan) => void;
}

interface ProductGroup {
  category: Category;
  plans: Plan[];
  available: Plan[];
  price: number | null;
}

const purchaseHelp = [
  {
    title: "如何订阅？",
    answer:
      "选择服务和套餐，登录账号后查看商品说明，确认订单金额并完成支付。交付内容可在“我的订单”中查看。",
  },
  {
    title: "价格与库存",
    answer:
      "页面显示人民币价格。实际应付金额、优惠和库存以提交订单时的确认结果为准。",
  },
  {
    title: "订单与售后",
    answer:
      "在“我的订单”中查看订单状态。遇到问题，请打开在线客服并提供订单号，我们会在这里跟进处理。",
  },
];

function PurchaseHelp() {
  const id = useId();
  const [openItem, setOpenItem] = useState<number | null>(null);
  return (
    <section className="bw-catalog-help" aria-label="购买说明">
      {purchaseHelp.map((item, index) => {
        const open = openItem === index;
        const questionId = `${id}-question-${index}`;
        const answerId = `${id}-answer-${index}`;
        return (
          <div className="bw-catalog-help-item" key={item.title}>
            <button
              type="button"
              className="bw-catalog-help-toggle"
              id={questionId}
              aria-expanded={open}
              aria-controls={answerId}
              onClick={() =>
                setOpenItem((current) => (current === index ? null : index))
              }
            >
              <span>{item.title}</span>
              <svg
                className="bw-catalog-help-chevron"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d={open ? "m18 15-6-6-6 6" : "m6 9 6 6 6-6"} />
              </svg>
            </button>
            <div
              className={`bw-catalog-help-answer${open ? " is-open" : ""}`}
              id={answerId}
              role="region"
              aria-labelledby={questionId}
              aria-hidden={!open}
              inert={!open}
            >
              <div>
                <p>{item.answer}</p>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
}

function canBuy(plan: Plan): boolean {
  return (
    plan.enabled === true &&
    plan.orderable === true &&
    plan.inStock === true &&
    typeof plan.displayAmount === "number" &&
    Number.isFinite(plan.displayAmount) &&
    plan.displayAmount >= 0 &&
    typeof plan.availableStock === "number" &&
    Number.isFinite(plan.availableStock) &&
    plan.availableStock > 0
  );
}

function categoryIds(category: Category): number[] {
  if (category.enabled === false) return [];
  return [category.id, ...(category.children || []).flatMap(categoryIds)];
}

function buildGroups(
  category: Category | undefined,
  plans: Plan[],
): ProductGroup[] {
  if (!category) return [];
  const children = (category.children || []).filter(
    (child) => child.enabled !== false,
  );
  return (children.length ? children : [category])
    .map((group) => {
      const ids = new Set(categoryIds(group));
      const groupPlans = plans.filter(
        (plan) => plan.enabled === true && ids.has(plan.categoryId),
      );
      const available = groupPlans.filter(canBuy);
      return {
        category: group,
        plans: groupPlans,
        available,
        price: available.length
          ? Math.min(...available.map((plan) => plan.displayAmount))
          : null,
      };
    })
    .filter((group) => group.plans.length > 0);
}

function ProductIcon({ category }: { category: Category }) {
  const icon = category.config?.icon || "";
  const brand = `${icon} ${category.service} ${category.name}`.toLowerCase();
  const gmail = category.service === "panda-mail" &&
    /(?:谷歌邮箱|gmail|google\s*mail)/i.test(category.name);
  const file = gmail
    ? "gmail-logo.svg"
    : brand.includes("claude")
    ? "landing-claude-CRJG2WdD.svg"
    : brand.includes("gemini")
      ? "gemini-dmEw2jOk.png"
      : brand.includes("gpt")
        ? "landing-gpt-D0FhbOei.svg"
        : brand.includes("grok") || brand.includes("gork")
          ? "grok-logo.png"
          : icon === "XIcon"
            ? "icon-x-BzZ2fsrd.png"
            : "";
  if (file)
    return <img src={`/assets/bewild/${file}`} alt="" aria-hidden="true" />;
  return (
    <span className="bw-catalog-letter" aria-hidden="true">
      {category.name.trim().slice(0, 1) || "P"}
    </span>
  );
}

function cardTitle(title: string, period = "") {
  let text = plainText(title);
  if (period && text.endsWith(period))
    text = text.slice(0, -period.length).trim();
  return text
    .replace(/^GPT\s+PLUS/, "ChatGPT Plus")
    .replace(/\s+质保(?=\s|$)/g, "")
    .replace(/\s+提供官方收据/g, "")
    .trim();
}

// Card content belongs to the product; SKU names and periods are order data.
// Official benefits checked 2026-09-08; sources: docs/SUBSCRIPTION-CARD-CONTENT.md.
const subscriptionCardDisplay = new Map<string, {
  tier: string;
  intro: string;
  features: string[];
}>([
  ["chatgpt go", {
    tier: "Go",
    intro: "日常学习与轻量创作",
    features: ["更多图片生成、文件上传", "文件阅读与数据分析", "更长记忆与对话上下文"],
  }],
  ["chatgpt plus", {
    tier: "Plus",
    intro: "进阶写作、研究与编程",
    features: ["高级推理，处理复杂任务", "深度研究与 Codex 编程", "进阶图像创作与自定义 GPT"],
  }],
  ["chatgpt pro 5x", {
    tier: "Pro 5x",
    intro: "面向高频研究与专业工作",
    features: ["Plus 的 5 倍用量额度", "Pro 推理，深入分析难题", "深度研究与 Codex 编程"],
  }],
  ["chatgpt pro 20x", {
    tier: "Pro 20x",
    intro: "更高额度，承接重度工作",
    features: ["Plus 的 20 倍用量额度", "Pro 推理，深入分析难题", "深度研究与 Codex 编程"],
  }],
]);

function ProductCard({
  category,
  title,
  subtitle,
  summary,
  description: customDescription,
  serviceOverview = false,
  showReceipt = false,
  plans,
  price,
  priceFrom = false,
  available,
  priceNote,
  onAction,
  actionLabel,
}: {
  category: Category;
  title: string;
  subtitle: string;
  summary?: string;
  description?: string;
  serviceOverview?: boolean;
  showReceipt?: boolean;
  plans: Plan[];
  price: number | null;
  priceFrom?: boolean;
  available: boolean;
  priceNote: string;
  onAction: () => void;
  actionLabel: string;
}) {
  // Separate the brand name from the Chinese product description without
  // dropping any part of the catalog title.
  const heading =
    title.match(/^([A-Za-z][A-Za-z0-9 .+\-]*?)\s+([\u4e00-\u9fff].*)$/) ||
    title.match(/^(.+?)([（(][^）)]+[）)])$/);
  const receipt = plans.every((plan) => plan.title.includes("提供官方收据"));
  const subscription = plans.length === 1 && plans[0].service === "panda-chatgpt"
    ? subscriptionCardDisplay.get(
        plainText(plans[0].productTitle)
          .replace(/\s+官方直充月卡$/, "")
          .toLowerCase(),
      )
    : undefined;
  const subscriptionPrice = subscription && price !== null && Number.isFinite(price)
    ? new Intl.NumberFormat("zh-CN", {
        minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(price)
    : null;
  const cardSummary = summary ?? heading?.[2];
  const description = customDescription ?? plainText(plans[0]?.description || plans[0]?.detail);
  return (
    <article className={`bw-product-card${serviceOverview ? " bw-service-card" : ""}${subscription ? " bw-subscription-card" : ""}${available ? "" : " is-unavailable"}`}>
      <header className="bw-product-heading">
        {subscription ? (
          <>
            <p className="bw-subscription-brand">ChatGPT</p>
            <div className="bw-subscription-title">
              <h3 title={plans[0].productTitle}>{subscription.tier}</h3>
              <span className="bw-subscription-term">月卡</span>
            </div>
          </>
        ) : (
          <>
            <span className="bw-product-icon">
              <ProductIcon category={category} />
            </span>
            <h3 title={title}>{heading ? heading[1] : title}</h3>
            {cardSummary && <p className="bw-product-subtitle">{cardSummary}</p>}
          </>
        )}
      </header>
      <div className="bw-product-details">
        {(subtitle || serviceOverview) && !subscription && (
          <p className="bw-product-spec" aria-hidden={!subtitle}>{subtitle}</p>
        )}
        {subscription ? (
          <>
            <p className="bw-subscription-intro">{subscription.intro}</p>
            <ul className="bw-subscription-benefits" aria-label="会员权益">
              {subscription.features.map((feature) => (
                <li key={feature}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <path d="m5 12 4 4L19 6" />
                  </svg>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </>
        ) : receipt && !serviceOverview && (
          <dl className="bw-product-facts" aria-label="商品信息">
            <div><dt>凭证</dt><dd>提供官方收据</dd></div>
          </dl>
        )}
        {description && (
          <p className="bw-product-description" title={description}>
            {description}
          </p>
        )}
      </div>
      <footer className="bw-product-footer">
        <div className="bw-product-pricing">
          <div className="bw-product-price">
            {price === null ? (
              <strong className="bw-product-no-price">暂时缺货</strong>
            ) : (
              <>
                <strong>
                  {subscriptionPrice !== null ? (
                    <><span className="bw-subscription-currency">¥</span>{subscriptionPrice}</>
                  ) : money(price)}
                </strong>
                {priceFrom && <span>起</span>}
              </>
            )}
          </div>
          {showReceipt && subscription && price !== null && (
            <p className="bw-product-price-note bw-subscription-receipt">提供官方收据</p>
          )}
          {priceNote && <p className="bw-product-price-note">{priceNote}</p>}
        </div>
        <button
          className="bw-product-buy"
          disabled={!available}
          onClick={onAction}
        >
          {available ? actionLabel : "暂时缺货"}
        </button>
      </footer>
    </article>
  );
}

export default function Catalog({
  catalog,
  loading,
  error,
  onRetry,
  onSelect,
}: CatalogProps) {
  const [tabId, setTabId] = useState<number | null>(null);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [choosingPlan, setChoosingPlan] = useState(false);
  const tabs = useMemo(
    () =>
      (catalog?.categoryTree || []).filter(
        (category) => category.enabled !== false,
      ),
    [catalog],
  );
  const tab = tabs.find((category) => category.id === tabId) || tabs[0];
  const groups = useMemo(
    () => buildGroups(tab, catalog?.plans || []),
    [tab, catalog],
  );
  const selected =
    groups.find(
      (group) => group.category.id === groupId && group.available.length,
    ) || groups.find((group) => group.available.length > 0);
  const selectedPlans = useMemo(() => {
    const plans = [...(selected?.plans || [])];
    if (selected?.category.service !== "panda-chatgpt") return plans;
    const go = plans.findIndex((plan) => /^ChatGPT Go(?:\s|$)/i.test(plainText(plan.productTitle)));
    const plus = plans.findIndex((plan) => /^ChatGPT Plus(?:\s|$)/i.test(plainText(plan.productTitle)));
    if (plus >= 0 && go > plus) {
      [plans[go], plans[plus]] = [plans[plus], plans[go]];
    }
    return plans;
  }, [selected]);

  function selectTab(id: number) {
    setTabId(id);
    setGroupId(null);
    setChoosingPlan(false);
  }

  function navigateTabs(
    event: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) {
    const direction =
      event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!direction && event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    const next =
      event.key === "Home"
        ? 0
        : event.key === "End"
          ? tabs.length - 1
          : (index + direction + tabs.length) % tabs.length;
    selectTab(tabs[next].id);
    const buttons =
      event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
        '[role="tab"]',
      );
    buttons?.[next]?.focus();
  }

  if (loading && !catalog)
    return (
      <main className="bw-catalog" aria-busy="true">
        <div className="bw-catalog-state" role="status">
          <span className="bw-catalog-spinner" />
          正在加载服务与套餐…
        </div>
      </main>
    );

  if (error && !catalog)
    return (
      <main className="bw-catalog">
        <div className="bw-catalog-state" role="alert">
          <h1>商品暂时加载失败</h1>
          <p>{error}</p>
          <button className="bw-catalog-primary" onClick={onRetry}>
            重新加载
          </button>
        </div>
      </main>
    );

  return (
    <main className="bw-catalog" aria-busy={loading}>
      <h1 className="bw-catalog-sr-only">订阅服务</h1>
      {error && (
        <div className="bw-catalog-warning" role="alert">
          <span>{error}</span>
          <button onClick={onRetry}>重新加载</button>
        </div>
      )}
      {tabs.length > 0 && (
        <div className="bw-catalog-tabs" role="tablist" aria-label="服务分类">
          {tabs.map((category, index) => (
            <button
              key={category.id}
              id={`bw-category-tab-${category.id}`}
              role="tab"
              aria-selected={category.id === tab?.id}
              aria-controls={`bw-category-panel-${category.id}`}
              tabIndex={category.id === tab?.id ? 0 : -1}
              onClick={() => selectTab(category.id)}
              onKeyDown={(event) => navigateTabs(event, index)}
            >
              {category.name}
            </button>
          ))}
        </div>
      )}

      <div
        id={tab ? `bw-category-panel-${tab.id}` : undefined}
        role={tab ? "tabpanel" : undefined}
        aria-labelledby={tab ? `bw-category-tab-${tab.id}` : undefined}
        className="bw-catalog-panel"
      >
        {groups.length === 0 ? (
          <div className="bw-catalog-state">
            <span className="bw-catalog-empty-symbol" aria-hidden="true">
              ◇
            </span>
            <h2>暂时没有上架商品</h2>
            <p>新的服务与套餐上架后会显示在这里。</p>
            <button className="bw-catalog-secondary" onClick={onRetry}>
              刷新商品
            </button>
          </div>
        ) : choosingPlan && selected ? (
          <>
            <div className="bw-catalog-plan-heading">
              <button
                className="bw-catalog-back"
                onClick={() => setChoosingPlan(false)}
              >
                <span aria-hidden="true">←</span> 返回服务选择
              </button>
              <h2>{selected.category.name}</h2>
              <p>选择套餐，继续查看商品说明与订单详情。</p>
            </div>
            <div
              className="bw-catalog-plan-grid"
              aria-label={`${selected.category.name}套餐`}
            >
              {selectedPlans.map((plan) => (
                <ProductCard
                  key={plan.id}
                  category={selected.category}
                  title={plan.productTitle ?? cardTitle(plan.title, plan.period)}
                  subtitle={plan.specName ?? plan.period ?? ""}
                  plans={[plan]}
                  showReceipt
                  price={plan.displayAmount}
                  available={canBuy(plan)}
                  priceNote={
                    canBuy(plan)
                      ? ""
                      : "补货后开放购买"
                  }
                  actionLabel="立即购买"
                  onAction={() => onSelect(plan)}
                />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="bw-catalog-grid" aria-label="选择服务">
              {groups.map((group) => {
                const single = group.plans.length === 1;
                const serviceOverview = tab?.service === "direct" &&
                  ["panda-chatgpt", "panda-claude", "panda-grok"].includes(group.category.service);
                const chatgptOverview =
                  group.category.service === "panda-chatgpt" &&
                  group.category.children.length > 1;
                const first = group.plans[0];
                const cheapest = group.available.reduce<Plan | undefined>(
                  (best, plan) =>
                    !best || plan.displayAmount < best.displayAmount
                      ? plan
                      : best,
                  undefined,
                );
                const productTitle =
                  group.category.children.length === 1
                    ? group.category.children[0].name
                    : group.category.name;
                return (
                  <ProductCard
                    key={group.category.id}
                    category={group.category}
                    title={single ? backendProductTitle(first) : productTitle}
                    summary={chatgptOverview ? "Go / Plus / Pro" : undefined}
                    serviceOverview={serviceOverview}
                    description={serviceOverview || chatgptOverview ? "官方直充 · 全程质保 · 可开发票" : undefined}
                    subtitle={
                      single
                        ? serviceOverview ? "" : first.specName ?? first.period ?? ""
                        : `${group.plans.length} 个套餐可选`
                    }
                    plans={group.plans}
                    price={group.price ?? (single ? first.displayAmount : null)}
                    priceFrom={!single && !!cheapest}
                    available={!!cheapest}
                    priceNote={
                      !cheapest
                        ? "补货后开放购买"
                        : single
                          ? ""
                          : cheapest.period
                            ? `起价套餐：${cheapest.period}`
                            : "按所选套餐结算"
                    }
                    actionLabel={single ? "立即购买" : "选择套餐"}
                    onAction={() => {
                      if (single && cheapest) onSelect(cheapest);
                      else {
                        setGroupId(group.category.id);
                        setChoosingPlan(true);
                      }
                    }}
                  />
                );
              })}
            </div>
            <PurchaseHelp />
          </>
        )}
      </div>
    </main>
  );
}
